import {CdkPortal, DomPortalOutlet} from '@angular/cdk/portal'
import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import {BehaviorSubject, Subject, takeUntil, tap} from 'rxjs'
import * as Diff2Html from 'diff2html'
import {diffLines} from 'diff'
import {LocalStorageService} from '@app/core/services/local-storage.service'
import {CdkDragEnd} from '@angular/cdk/drag-drop'

export interface DebugDataTemplate {
  windowName: string
  tabs: DebugDataTabTemplate[]
}

export interface DebugDataTabTemplate {
  tabName: string
  tabContent: any // fine for now
  tabContentType: 'JSON' | 'TEMPLATE' | 'HTML' | 'STRING' | any
  comparableWithTabNames: string[]
}
@Component({
  selector: 'app-debug',
  templateUrl: './app-debug.component.html',
  styleUrls: ['./app-debug.component.scss'],
  standalone: false,
})
export class AppDebugComponent implements OnDestroy, AfterViewInit {
  @Input() debugData$ = new BehaviorSubject<DebugDataTemplate>(null)
  diffCheckData$ = new BehaviorSubject<DebugDataTemplate>(null)
  @Input() hideWindow: boolean = false
  @Input() portalToHTMLBody: boolean = true
  @Input() initialWidth: number = 800
  @Input() initialHeight: number = 800
  @Output() onWindowClose = new EventEmitter()

  @ViewChild('dragWindow') dragWindow: any // CdkDragHandle
  @ViewChild(CdkPortal, {static: false}) portalContentTemplate!: CdkPortal

  end$ = new Subject<void>()
  debugNotification$ = new BehaviorSubject<string>('')
  activeTabIndex = 0
  minimizeWindow: boolean = false
  isResizing = false
  domPortalOutlet: DomPortalOutlet
  windowName: string = ''
  initialized = true // should be set to false when we load from storage settings first
  // storage stuff
  storageKeyPrefix = '_debug_'
  savePositionDebounceTime = 300 // milliseconds
  savePosition$ = new Subject<void>()

  constructor(
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private localStorageService: LocalStorageService,
  ) {}

  openComparison(a, b) {
    const left = JSON.stringify(
      this.debugData$.value.tabs.find((e) => e.tabName === a).tabContent,
      null,
      2,
    )
    const right = JSON.stringify(
      this.debugData$.value.tabs.find((e) => e.tabName === b).tabContent,
      null,
      2,
    )
    const diff = diffLines(left, right)

    let diffString = ''

    diff.forEach((part) => {
      const lines = part.value.split('\n')
      lines.forEach((line) => {
        if (line !== '') {
          let prefix = ' '
          if (part.added) {
            prefix = '+'
          } else if (part.removed) {
            prefix = '-'
          }
          diffString += prefix + ' ' + line + '\n'
        }
      })
    })

    // Construct the Diff2Html input string WITH the context line
    const fileNameA = `a/${a}`
    const fileNameB = `b/${b}`
    // trial and error, issues in here makes Diff2Html have parsing issues and itll report no changes
    // this setup currently renders, its not perfect but itll do
    const header = `--- ${fileNameA}\n+++ ${fileNameB}\n@@ -1,${left.split('\n').length} +1,${right.split('\n').length} @@\n`
    const diff2htmlInput = header + diffString

    let outputHtml = Diff2Html.html(diff2htmlInput)

    this.diffCheckData$.next({
      windowName: 'Diff Checker V0.1',
      tabs: [
        {
          tabName: a + ' VS ' + b,
          tabContentType: 'HTML',
          tabContent: outputHtml,
          comparableWithTabNames: [],
        },
      ],
    })
  }

  ngAfterViewInit(): void {
    // this.debugData$
    // .pipe(
    //   takeUntil(this.end$),
    //   tap((data) => {
    //     if (data?.windowName) {
    //       this.windowName = data.windowName;
    //       this.loadWindowSettings();

    //     }
    //   })
    // )
    // .subscribe();

    if (this.portalToHTMLBody) {
      this.createDomPortalOutlet()
    }

    this.debugNotification$
      .pipe(
        takeUntil(this.end$),
        tap((notification) => {
          if (notification !== '') {
            setTimeout(() => {
              this.debugNotification$.next('')
            }, 1500)
          }
        }),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.end$.next()
    this.end$.complete()
    if (this.domPortalOutlet) {
      this.domPortalOutlet.detach()
    }
  }
  minimize() {
    this.minimizeWindow = true
  }

  expand() {
    this.minimizeWindow = false
  }

  close() {
    this.hideWindow = true
    this.onWindowClose.emit()
  }

  selectTab(index: number) {
    this.activeTabIndex = index
  }

  copyContentToClipboard() {
    const debugData = this.debugData$.value
    const activeTabData = debugData.tabs[this.activeTabIndex]
    let content = ''
    if (activeTabData.tabContentType === 'JSON') {
      content = JSON.stringify(activeTabData.tabContent, null, 2)
    } else if (activeTabData.tabContentType === 'HTML') {
      content = activeTabData.tabContent.toString()
    } else if (activeTabData.tabContentType === 'TEMPLATE') {
      // yeh not sure what to do with this yet
    } else if (activeTabData.tabContentType === 'STRING') {
      content = activeTabData.tabContent
    }

    document.addEventListener('copy', (e: ClipboardEvent) => {
      e.clipboardData.setData('text/plain', content)
      e.preventDefault()
      document.removeEventListener('copy', null)
      this.debugNotification$.next('Copied to clipboard!')
    })
    document.execCommand('copy')
  }

  onResizeMouseDown(event: MouseEvent): void {
    this.isResizing = true

    event.preventDefault()
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    this.isResizing = false
    this.savePosition$.next() // Trigger save on mouse up after potential resize/drag
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      const windowElement = this.dragWindow?.nativeElement // Use ViewChild to get the element reference
      if (windowElement) {
        const newWidth =
          event.clientX - windowElement.getBoundingClientRect().left
        const newHeight =
          event.clientY - windowElement.getBoundingClientRect().top
        if (newWidth > 350 && newHeight > 200) {
          windowElement.style.width = `${newWidth}px`
          windowElement.style.height = `${newHeight}px`
          this.savePosition$.next() // Trigger save during resize
        }
      }
    }
  }

  // Listen for drag end event
  onDragEnded(event: CdkDragEnd): void {
    this.savePosition$.next() // Trigger save when dragging ends
  }

  private getStorageKey(): string {
    return this.storageKeyPrefix + this.windowName
  }

  private loadWindowSettings(): void {
    const storedSettings = this.localStorageService.getObject(
      this.getStorageKey(),
    )
    if (storedSettings) {
      if (this.dragWindow?.nativeElement) {
        this.dragWindow.nativeElement.style.transform =
          storedSettings.position || ''
        this.dragWindow.nativeElement.style.width = storedSettings.size?.width
          ? `${storedSettings.size.width}px`
          : `${this.initialWidth}px`
        this.dragWindow.nativeElement.style.height = storedSettings.size?.height
          ? `${storedSettings.size.height}px`
          : `${this.initialHeight}px`
      }
    } else {
      // Create default entry if it doesn't exist
      this.saveWindowSettings({
        position: '',
        size: {width: this.initialWidth, height: this.initialHeight},
      })
      if (this.dragWindow?.nativeElement) {
        this.dragWindow.nativeElement.style.width = `${this.initialWidth}px`
        this.dragWindow.nativeElement.style.height = `${this.initialHeight}px`
      }
    }
    this.initialized = true
  }

  private saveWindowSettings(overrideSettings: any = null): void {
    if (this.windowName && this.dragWindow?.nativeElement) {
      const element = this.dragWindow.nativeElement
      const currentTransform = element.style.transform
      const currentWidth = element.offsetWidth
      const currentHeight = element.offsetHeight

      const settingsToSave = overrideSettings || {
        position: currentTransform,
        size: {width: currentWidth, height: currentHeight},
      }

      this.localStorageService.setObject(this.getStorageKey(), settingsToSave)
    }
  }

  private createDomPortalOutlet() {
    // Use the <body> tag directly as the outlet element
    const outletElement = document.body

    // Create a DomPortalOutlet using the <body> tag
    this.domPortalOutlet = new DomPortalOutlet(
      outletElement,
      this.appRef,
      this.injector,
    )

    // Attach the portal to the outlet
    this.domPortalOutlet.attach(this.portalContentTemplate)
  }
}
